import { SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from './Entities'

export enum Permission {
  // OVERVIEW PAGE
  viewOverview = 'view overview',

  // USERS
  listUser = 'list user',
  readUser = 'read user',
  updateUser = 'update user',
  deleteUser = 'delete user',

  // RESERVATIONS
  listReservation = 'list reservation',

  // VENUES
  listVenue = 'list venue',
  createVenue = 'create venue',
  readVenue = 'read venue',
  updateVenue = 'update venue',
  deleteVenue = 'delete venue',

  // TAGS
  listTag = 'list venueTag',
  createTag = 'create venueTag',
  readTag = 'read venueTag',
  updateTag = 'update venueTag',
  deleteTag = 'delete venueTag',

  // CLUSTERS
  listCluster = 'list cluster',
  createCluster = 'create cluster',
  readCluster = 'read cluster',
  updateCluster = 'update cluster',
  deleteCluster = 'delete cluster',

  // AUDIENCES
  listAudience = 'list audience',
  createAudience = 'create audience',
  readAudience = 'read audience',
  updateAudience = 'update audience',
  deleteAudience = 'delete audience',

  // PUSH
  listPush = 'list push',
  createPush = 'create push',
  readPush = 'read push',
  updatePush = 'update push',
  deletePush = 'delete push',
  sendPush = 'send push',

  // LOYALTY TIERS
  listLoyaltyTier = 'list loyaltyTier',
  createLoyaltyTier = 'create loyaltyTier',
  readLoyaltyTier = 'read loyaltyTier',
  updateLoyaltyTier = 'update loyaltyTier',
  deleteLoyaltyTier = 'delete loyaltyTier',

  // REFERRAL
  listReferral = 'list referral',
  updateReferral = 'update referral',
  addCreditsReferral = 'add credits referral',

  // FEATURED VENUES
  listFeaturedVenue = 'list featuredVenue',
  createFeaturedVenue = 'create featuredVenue',
  readFeaturedVenue = 'read featuredVenue',
  updateFeaturedVenue = 'update featuredVenue',
  deleteFeaturedVenue = 'delete featuredVenue',

  // PLATFORMS
  listPlatform = 'list platform',
  editSettingsPlatform = 'edit settings platform',

  // INTEGRATIONS
  manageIntegrationUber = 'manage integration uber',

  // // MEMBERSHIP
  manageProgramsMembership = 'manage programs membership',
  manageRequestsMembership = 'manage requests membership',
  manageMembersMembership = 'manage members membership',
  manageEventsMembership = 'manage events membership',
  manageCouponsMembership = 'manage coupons membership',
  managePerksMembership = 'manage perks membership',
  viewRevenueMembership = 'view revenue membership',

  // SEATING AREAS
  listSeatingArea = 'list seatingArea',
  createSeatingArea = 'create seatingArea',
  readSeatingArea = 'read seatingArea',
  updateSeatingArea = 'update seatingArea',

  // ADMINS
  listAdmin = 'list admin',
  createAdmin = 'create admin',
  deleteAdmin = 'delete admin',
  manageRolesAdmin = 'manage roles admin',

  // BOOKING SETTINGS
  manageSettingsBookingSettings = 'manage settings booking settings',

  // CHAT
  manageVisibilityChat = 'manage visibility chat',

  // MEMBERSHIP SETTINGS
  manageSettingsMembershipSettings = 'manage settings membership settings',

  // Business
  manageBusiness = 'manage business',

  // COMPLEMENTARY ITEMS
  manageComplementaryItems = 'manage complementary items',

  // TOOLS
  accessAvailabilityViewer = 'access availability viewer',
  accessVenueBilling = 'access venue billing',

  // API
  manageApiKeys = 'manage api keys',

  // LEGAL DOCUMENTS
  listLegalDocument = 'list legal document',
  createLegalDocument = 'create legal document',
  readLegalDocument = 'read legal document',
  updateLegalDocument = 'update legal document',
  deleteLegalDocument = 'delete legal document',
}

export function enumFromStringValue<T>(
  enm: { [s: string]: T },
  value: string
): T | undefined {
  return (Object.values(enm) as unknown as string[]).includes(value)
    ? (value as unknown as T)
    : undefined
}

export function permissionFromEntityAndAction(
  entity: Entity,
  action: SBRMAction
): Permission {
  // Default way to compute Permission
  const verb = (() => {
    switch (action) {
      // case 'update':
      //   return 'edit'
      case 'duplicate':
        return 'create'
      case 'view':
        return 'read'
      default:
        return action
    }
  })()

  if (entity === Entity.menu || entity === Entity.venuesCollection) {
    return Permission.updateVenue
  }

  if (entity === Entity.eventParticipationStatus) {
    return Permission.manageSettingsMembershipSettings
  }

  if (entity === Entity.coupon) {
    return Permission.manageCouponsMembership
  }

  if (entity === Entity.perk) {
    return Permission.managePerksMembership
  }

  if (entity === Entity.membershipProgramCurrency) {
    return Permission.manageProgramsMembership
  }

  if (
    entity === Entity.complementaryItem ||
    entity === Entity.programComplementaryItem
  ) {
    return Permission.manageComplementaryItems
  }

  if (
    entity === Entity.event ||
    entity === Entity.eventParticipation ||
    entity === Entity.eventsCollection
  ) {
    return Permission.manageEventsMembership
  }

  if (
    entity === Entity.membershipSubscriptions ||
    entity === Entity.membershipProfileTag ||
    entity === Entity.subscriptionTag ||
    entity === Entity.membershipProfileComplementaryItem ||
    entity === Entity.membershipProfileCurrency ||
    entity === Entity.membershipProfiles
  ) {
    return Permission.manageMembersMembership
  }

  // Analytics read-models (ticket 537). Mirrors what the server actually
  // enforces: SubscriptionPaymentController and SubscriptionController both
  // authorize MANAGE_REQUESTS_MEMBERSHIP in their constructors, so this is also
  // the permission the pages hosting these tables gate on. Without a case here
  // at all, the default lookup builds "view membershipTransaction", which is no
  // Permission, and the check fails silently for everyone.
  if (entity === Entity.membershipTransaction) {
    return Permission.manageRequestsMembership
  }

  // Segments are custom audiences: they hit /audiences and are authorized by
  // the audience permissions server-side (AudienceController).
  if (entity === Entity.segment) {
    return enumFromStringValue(Permission, verb + ' ' + Entity.audience)!
  }

  if (entity === Entity.dominoConfig || entity === Entity.businessVerifier) {
    return Permission.manageBusiness
  }

  if (
    entity === Entity.sevenroomsAudience ||
    entity === Entity.sevenroomsVenueGroup
  ) {
    return Permission.createAdmin
  }

  if (
    entity === Entity.company ||
    entity === Entity.policy ||
    entity === Entity.companyContact ||
    entity === Entity.businessProfile
  ) {
    return Permission.manageBusiness
  }

  if (entity === Entity.admin && action === SBRMAction.Update) {
    return Permission.manageRolesAdmin
  }

  if (entity === Entity.externalBookingAPIKey) {
    return Permission.manageApiKeys
  }

  if (
    entity === Entity.legalDocument ||
    entity === Entity.legalDocumentVersion
  ) {
    return Permission.updateLegalDocument
  }

  if (entity === Entity.notification) {
    return enumFromStringValue(Permission, verb + ' push')!
  }

  return enumFromStringValue(Permission, verb + ' ' + entity)!
}
